import { useRef, type ChangeEvent } from 'react';
import {
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  Trash2,
  RefreshCw,
  FileText,
} from 'lucide-react';
import type { ScholarshipDocumentSlot, DocumentSlotState } from './types';
import { validateFile, formatFileSize } from './validation';

interface DocumentUploadCardProps {
  slot: ScholarshipDocumentSlot;
  title: string;
  state: DocumentSlotState;
  onSelectFile: (slot: ScholarshipDocumentSlot, file: File) => void;
  onRemove: (slot: ScholarshipDocumentSlot) => void;
  onClientError: (slot: ScholarshipDocumentSlot, error: string, file: File) => void;
  isAnyUploadInProgress: boolean;
}

export default function DocumentUploadCard({
  slot,
  title,
  state,
  onSelectFile,
  onRemove,
  onClientError,
  isAnyUploadInProgress,
}: DocumentUploadCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input value so re-selecting same file triggers change
    e.target.value = '';

    // Client-side validation before upload
    const clientError = validateFile(file);
    if (clientError) {
      onClientError(slot, clientError, file);
      return;
    }

    onSelectFile(slot, file);
  };

  const triggerFileInput = () => {
    if (isAnyUploadInProgress || state.status === 'uploading') return;
    fileInputRef.current?.click();
  };

  const progress = state.progress ?? 0;

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        className="sr-only"
        onChange={handleFileChange}
        disabled={isAnyUploadInProgress || state.status === 'uploading'}
      />

      {/* Empty State */}
      {state.status === 'empty' && (
        <div
          onClick={triggerFileInput}
          className="border-2 border-dashed border-gray-300 hover:border-[#5B3FD0] bg-white hover:bg-[#5B3FD0]/5 rounded-2xl p-5 text-center transition-all duration-200 cursor-pointer flex flex-col items-center justify-center gap-2 group"
        >
          <div className="w-11 h-11 rounded-xl bg-[#5B3FD0]/10 text-[#5B3FD0] flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
            <UploadCloud className="w-6 h-6" />
          </div>
          <p className="text-sm sm:text-base font-semibold text-[#2C2C2C]">
            {title}
          </p>
          <p className="text-xs text-[#707070]">
            PDF أو صورة — حد أقصى ٢٠ ميجا
          </p>
          <button
            type="button"
            className="mt-1 px-4 py-2 text-xs font-semibold text-[#5B3FD0] bg-[#5B3FD0]/10 rounded-xl group-hover:bg-[#5B3FD0] group-hover:text-white transition-colors duration-200 pointer-events-none"
          >
            اختار الملف
          </button>
        </div>
      )}

      {/* Validating / Uploading State */}
      {state.status === 'uploading' && (
        <div className="border-2 border-[#5B3FD0]/30 bg-[#5B3FD0]/5 rounded-2xl p-5 transition-all">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <FileText className="w-5 h-5 text-[#5B3FD0] shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#5B3FD0]">{title}</p>
                <p className="text-xs sm:text-sm font-medium text-[#2C2C2C] truncate max-w-[200px] sm:max-w-xs">
                  {state.fileName}
                </p>
              </div>
            </div>
            <span dir="ltr" className="text-xs font-bold text-[#5B3FD0] shrink-0">
              {progress}%
            </span>
          </div>

          {/* Determinate progress bar */}
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#5B3FD0] rounded-full transition-all duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-[#707070] text-center">
            بنرفع الملف ونتأكد منه...
          </p>
        </div>
      )}

      {/* Success State */}
      {state.status === 'success' && (
        <div className="border-2 border-emerald-500/30 bg-emerald-50/20 rounded-2xl p-4 sm:p-5 transition-all">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-emerald-100/70 text-emerald-600 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-[#707070] font-medium">{title}</p>
                <p
                  title={state.fileName}
                  className="text-xs sm:text-sm font-bold text-[#2C2C2C] truncate max-w-[160px] sm:max-w-[240px]"
                >
                  {state.fileName}
                </p>
                <span dir="ltr" className="text-xs text-[#707070] font-mono inline-block">
                  {formatFileSize(state.fileSize)}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <button
                type="button"
                onClick={triggerFileInput}
                disabled={isAnyUploadInProgress}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#5B3FD0] hover:bg-[#5B3FD0]/10 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>استبدال</span>
              </button>
              <button
                type="button"
                onClick={() => onRemove(slot)}
                disabled={isAnyUploadInProgress}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#F46A6A] hover:bg-[#F46A6A]/10 disabled:opacity-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>حذف</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {state.status === 'error' && (
        <div className="border-2 border-[#F46A6A] bg-[#F46A6A]/5 rounded-2xl p-4 sm:p-5 transition-all">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-[#F46A6A] flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#2C2C2C]">{title}</p>
                {state.fileName && (
                  <p
                    title={state.fileName}
                    className="text-xs sm:text-sm font-medium text-[#2C2C2C] truncate max-w-[160px] sm:max-w-[240px] mt-0.5"
                  >
                    {state.fileName}
                    {state.fileSize ? (
                      <span dir="ltr" className="text-xs text-[#707070] font-mono ms-2">
                        ({formatFileSize(state.fileSize)})
                      </span>
                    ) : null}
                  </p>
                )}
                <p className="text-xs sm:text-sm font-medium text-[#F46A6A] mt-0.5 leading-relaxed">
                  {state.errorMessage || 'الملف مارفعش. جرب تاني.'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={triggerFileInput}
              disabled={isAnyUploadInProgress}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#F46A6A] hover:bg-[#e05757] disabled:opacity-50 transition-colors shrink-0"
            >
              حاول تاني
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
